import { useNavigate, useParams } from 'react-router-dom'
import AgentForm from '@/components/Agents/AgentForm'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { agentService } from '@/api/services/agent.service'
import { useGetDraftByAgentId } from '@/api/hooks/useAgent'
import type {
  BaseAgentRequest,
  UpdateAgentRequest,
} from '@/api/types/agent.type'
import { KnowledgeDocType } from '@/api/types/knowledge.types'
import { formatDateTime } from '@/lib/utils'

const EditAgent = () => {
  const { agentId } = useParams<{ agentId: string }>()
  const { currentWorkspace } = useWorkspaceStore()
  const navigate = useNavigate()

  // 使用 hook 获取智能体详情
  const {
    data: agent,
    error: agentError,
    isLoading: loading,
  } = useGetDraftByAgentId({
    agentId: agentId || '',
  })

  // 处理错误状态
  const error = agentError ? '获取智能体详情失败，请稍后重试' : null

  // 处理更新智能体
  const handleUpdate = async (agentRequest: BaseAgentRequest) => {
    if (!agent) return

    try {
      console.log('更新智能体', agentRequest)

      // 构建更新请求参数，使用获取到的详情中的 id
      const updateRequest: UpdateAgentRequest = {
        ...agentRequest,
        id: String(agent.id),
      }

      // 调用API更新智能体
      await agentService.updateAgent(updateRequest)

      // 更新成功后跳转到列表页面
      navigate('/agents/list')
    } catch (error) {
      console.error('更新智能体失败:', error)
      // TODO: 显示错误提示
    }
  }

  // 处理取消
  const handleCancel = () => {
    console.log('取消编辑')
    navigate('/agents/list')
  }

  // 转换智能体数据为表单数据
  const getInitialData = () => {
    // 只有在非加载状态且有 agent 数据时才返回数据
    if (loading || !agent) return undefined

    return {
      name: agent.name || '',
      category: agent.type || 1,
      setting: agent.setting || '',
      description: agent.description || '',
      greeting: agent.greeting || '',
      questionSuggestions: agent.questionSuggestionList || [''],
      model: agent.modelId || 0,
      temperature: agent.modelSettingVo?.temperature || 0.7,
      roundsContext: agent.modelSettingVo?.roundsContext || 5,
      maxReplyTokens: agent.modelSettingVo?.maxReplyTokens || 2000,
      skills: agent.skillTypeList || [],
      knowledgeBase: KnowledgeDocType.TEXT, // 默认值，实际应该根据知识库类型确定
      icon: agent.icon || '',
      knowledgeBaseIdList: agent.knowledgeBaseIdList || [], // 添加知识库ID列表
    }
  }

  // 加载状态优先显示
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">加载中...</div>
      </div>
    )
  }

  // 显示错误状态
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg text-red-600 mb-4">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            重新加载
          </button>
        </div>
      </div>
    )
  }

  // 只有在非加载状态且没有 agent 数据时才显示"智能体不存在"
  if (!agent) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">智能体不存在</div>
      </div>
    )
  }

  return (
    <AgentForm
      initialData={getInitialData()}
      onSubmit={handleUpdate}
      onCancel={handleCancel}
      submitButtonText="保存"
      cancelButtonText="取消"
      workspaceId={currentWorkspace?.id || ''}
      title="编辑智能体"
      subtitle={
        agent.isUpdated
          ? `编辑于：${formatDateTime(agent.updateTime || '')}`
          : undefined
      }
    />
  )
}

export default EditAgent
